import { useEffect, useMemo, useState } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { Show } from '../../types/Show';
import { ApiService } from '../../services/apiService';
import { groupShowsByDate } from '../../utils/dateUtils';
import { cachingStrategyService, DataType } from '../../services/cachingStrategyService';
import { debugError, debugVerbose, debugWarn } from '../../utils/debugLog';

export const SHOWS_QUERY_KEY = ['shows'] as const;

// Filter types for shows
export interface ShowsFilters {
  presenter?: string;
  genre?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  dayOfWeek?: string;
  hasAudio?: boolean;
  sort?: 'newest' | 'oldest';
}

// Options for useShowsData hook
export interface ShowsDataOptions {
  station_id?: string;
  limit?: number;
  filters?: ShowsFilters;
  enabled?: boolean;
  useInfiniteScroll?: boolean;
}

// Performance metrics from API
export interface ShowsPerformance {
  queryTime: number;
  efficiency: number;
  recommendation?: string;
  cached: boolean;
  cacheHit: boolean;
  details?: {
    totalTime: number;
    dynamoDbTime: number;
    filterTime: number;
    batchCount: number;
    queryStrategy: string;
    gsiUsed?: string;
  };
  classification: string;
  estimatedCost: {
    rcuUsed: number;
    estimatedCostUSD: number;
    costCategory: string;
  };
}

// Pagination info from API
export interface ShowsPagination {
  limit: number;
  hasMore: boolean;
  lastKey?: string;
  totalReturned: number;
  totalScanned: number;
}

// Available filters from API
export interface AvailableFilters {
  presenters: string[];
  genres: string[];
}

// API response structure
export interface ShowsApiResponse {
  shows: Show[];
  pagination: ShowsPagination;
  filters: {
    applied: ShowsFilters;
    available: AvailableFilters;
  };
  performance: ShowsPerformance;
}

// Legacy return type for backward compatibility
export interface UseShowsDataReturn {
  shows: Show[];
  groupedShows: { [key: string]: Show[] };
  loading: boolean;
  error: string | null;
  refetch: () => Promise<any>;
}

// Enhanced return type for infinite scroll
export interface UseShowsDataInfiniteReturn {
  shows: Show[];
  groupedShows: { [key: string]: Show[] };
  loading: boolean;
  error: string | null;
  refetch: () => Promise<any>;
  loadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  performance?: ShowsPerformance;
  pagination?: ShowsPagination;
  availableFilters?: AvailableFilters;
}

/**
 * Enhanced data fetching hook for shows with infinite scroll pagination
 * 
 * This hook provides both legacy compatibility and new infinite scroll functionality.
 * It automatically detects whether to use infinite scroll based on options.
 * 
 * Features:
 * - Infinite scroll pagination with React Query
 * - Advanced filtering (presenter, genre, search, date range, etc.)
 * - Performance monitoring and efficiency tracking
 * - Smart caching with 5-minute TTL
 * - GSI optimization for efficient queries
 * - Backward compatibility with existing code
 * 
 * @param options Configuration options for the hook
 * @returns Shows data with pagination and performance metrics
 */
export function useShowsData(options: ShowsDataOptions & { useInfiniteScroll: true }): UseShowsDataInfiniteReturn;
export function useShowsData(options?: ShowsDataOptions): UseShowsDataReturn;
export function useShowsData(options: ShowsDataOptions = {}): UseShowsDataReturn | UseShowsDataInfiniteReturn {
  const {
    station_id = 'eruption',
    limit = 20,
    filters = {},
    enabled = true,
    useInfiniteScroll = false
  } = options;

  const apiFilters = useMemo(() => {
    if (!filters || typeof filters !== 'object') {
      return {};
    }

    if (!('sort' in filters)) {
      return filters;
    }

    const { sort: _sort, ...rest } = filters as ShowsFilters;
    return rest;
  }, [filters]);

  // Apply centralized caching strategy for past shows data
  const cacheStrategy = cachingStrategyService.getStrategy(DataType.PAST_SHOWS_DATA) || {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: 1000,
  };

  // Use separate cache strategy for infinite scroll to prevent stale pagination cursors
  const infiniteScrollCacheStrategy = cachingStrategyService.getStrategy(DataType.PAST_SHOWS_INFINITE_SCROLL) || {
    staleTime: 0, // Always fetch fresh for pagination
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: 1000,
  };

  // Legacy single-page query for backward compatibility
  const legacyQuery = useQuery({
    queryKey: [...SHOWS_QUERY_KEY, 'legacy'],
    queryFn: async (): Promise<Show[]> => {
      const response = await ApiService.getShows();
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch shows');
      }
      return response.data;
    },
    // Apply centralized caching strategy
    staleTime: cacheStrategy.staleTime,
    gcTime: cacheStrategy.gcTime,
    refetchOnWindowFocus: cacheStrategy.refetchOnWindowFocus,
    refetchOnReconnect: cacheStrategy.refetchOnReconnect,
    retry: cacheStrategy.retry,
    retryDelay: cacheStrategy.retryDelay,
    // FIXED: Only enable legacy query when NOT using infinite scroll
    enabled: enabled && !useInfiniteScroll,
  });

  // Create stable query key for infinite scroll to prevent cache collisions
  const infiniteScrollQueryKey = useMemo(() => {
    // Serialize filters to ensure consistent cache keys
    const serializedFilters = apiFilters ? JSON.stringify(apiFilters) : 'no-filters';
    return [...SHOWS_QUERY_KEY, 'infinite', station_id, limit, serializedFilters];
  }, [station_id, limit, apiFilters]);

  const [infiniteError, setInfiniteError] = useState<string | null>(null);

  // New infinite scroll query
  const infiniteQuery = useInfiniteQuery({
    queryKey: infiniteScrollQueryKey,
    queryFn: async ({ pageParam }): Promise<ShowsApiResponse> => {
      debugVerbose('🔍 useShowsData queryFn called with pageParam:', pageParam);

      const response = await ApiService.getShowsPaginated({
        station_id,
        limit,
        lastKey: pageParam,
        filters: apiFilters,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch shows');
      }

      debugVerbose('🔍 useShowsData API response:', {
        inputCursor: pageParam,
        outputCursor: response.data.pagination.lastKey,
        hasMore: response.data.pagination.hasMore,
        showsReturned: response.data.shows.length,
        totalReturned: response.data.pagination.totalReturned,
        totalScanned: response.data.pagination.totalScanned,
        cursorChanged: response.data.pagination.lastKey !== pageParam,
      });

      return response.data;
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const currentLastKey = lastPage.pagination.lastKey;
      const hasMore = lastPage.pagination.hasMore;
      
      // Check if React Query is serving stale/duplicate pages
      if (allPages.length > 1) {
        const currentPageShows = lastPage.shows.map(s => s.show_id);
        const previousPagesShows = allPages.slice(0, -1).flatMap(page => 
          page.shows.map(s => s.show_id)
        );
        
        const duplicatesInCurrentPage = currentPageShows.filter(id => 
          previousPagesShows.includes(id)
        );
        
        if (duplicatesInCurrentPage.length > 0) {
          debugError('🚨 REACT QUERY CACHE ISSUE: Current page contains shows from previous pages!', {
            duplicateShows: duplicatesInCurrentPage,
            currentPageShows: currentPageShows.length,
            totalPreviousShows: previousPagesShows.length,
            currentCursor: currentLastKey,
            previousCursor: lastPageParam
          });
          
          // This indicates React Query is serving stale cached data
          // We should invalidate the query and restart
          return undefined; // Stop pagination to prevent further corruption
        }
      }
      
      debugVerbose('🔍 useShowsData getNextPageParam analysis:', {
        currentLastKey,
        lastPageParam,
        hasMore,
        totalPages: allPages.length,
        cursorSame: currentLastKey === lastPageParam,
        shouldStop: !hasMore || !currentLastKey,
        decision: (!hasMore || !currentLastKey) ? 'STOP' : 'CONTINUE'
      });
      
      // REVISED LOGIC: Only stop if hasMore is false OR no cursor provided
      if (!hasMore) {
        debugVerbose('🛑 useShowsData: Stopping pagination - API says no more data');
        return undefined;
      }
      
      if (!currentLastKey) {
        debugVerbose('🛑 useShowsData: Stopping pagination - no cursor provided');
        return undefined;
      }
      
      debugVerbose('✅ useShowsData: Continuing pagination with cursor:', currentLastKey);
      return currentLastKey;
    },
    initialPageParam: null as string | null,
    staleTime: infiniteScrollCacheStrategy.staleTime,
    gcTime: infiniteScrollCacheStrategy.gcTime,
    refetchOnWindowFocus: infiniteScrollCacheStrategy.refetchOnWindowFocus,
    refetchOnReconnect: infiniteScrollCacheStrategy.refetchOnReconnect,
    retry: infiniteScrollCacheStrategy.retry,
    retryDelay: infiniteScrollCacheStrategy.retryDelay,
    enabled: enabled && useInfiniteScroll,
  });

  useEffect(() => {
    if (!useInfiniteScroll || !enabled) {
      setInfiniteError(null);
      return;
    }

    if (infiniteQuery.error) {
      const err = infiniteQuery.error;
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'An error occurred';
      setInfiniteError(message);
      return;
    }

    if (!infiniteQuery.isPending && !infiniteQuery.isFetching) {
      setInfiniteError(null);
    }
  }, [
    enabled,
    useInfiniteScroll,
    infiniteQuery.error,
    infiniteQuery.isPending,
    infiniteQuery.isFetching,
  ]);

  const applyDateFilters = useMemo(() => {
    const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const toDate = filters.dateTo ? new Date(filters.dateTo) : null;

    if (toDate) {
      toDate.setHours(23, 59, 59, 999);
    }

    return (list: Show[]) => {
      if (!fromDate && !toDate) {
        return list;
      }

      return list.filter((show) => {
        const showDate = new Date(show.date_start_time.replace('#', 'T'));
        if (fromDate && showDate < fromDate) {
          return false;
        }
        if (toDate && showDate > toDate) {
          return false;
        }
        return true;
      });
    };
  }, [filters.dateFrom, filters.dateTo]);

  // Memoized flattened shows for infinite scroll
  const infiniteShows = useMemo(() => {
    if (!infiniteQuery.data?.pages.length) return [];
    
    const allShows = infiniteQuery.data.pages.flatMap(page => page.shows);
    
    // Detailed duplication analysis
    const showIds = allShows.map(show => show.show_id);
    const uniqueIds = Array.from(new Set(showIds));
    const duplicateCount = showIds.length - uniqueIds.length;
    
    debugVerbose('🔍 useShowsData show accumulation analysis:', {
      totalPages: infiniteQuery.data.pages.length,
      totalShows: allShows.length,
      uniqueShows: uniqueIds.length,
      duplicates: duplicateCount,
      pagesBreakdown: infiniteQuery.data.pages.map((page, index) => ({
        page: index,
        shows: page.shows.length,
        cursor: page.pagination.lastKey,
        hasMore: page.pagination.hasMore,
        firstShow: page.shows[0]?.date_start_time,
        lastShow: page.shows[page.shows.length - 1]?.date_start_time
      }))
    });
    
    if (duplicateCount > 0) {
      debugWarn('⚠️ DUPLICATION DETECTED:', {
        duplicateCount,
        totalShows: allShows.length,
        uniqueShows: uniqueIds.length
      });
      
      // Find which shows are duplicated
      const duplicatedIds = showIds.filter((id, index) => showIds.indexOf(id) !== index);
      debugWarn('⚠️ Duplicated show IDs:', Array.from(new Set(duplicatedIds)));
    }
    
    return applyDateFilters(allShows);
  }, [infiniteQuery.data, applyDateFilters]);

  // Memoized performance metrics
  const performance = useMemo(() => {
    if (!infiniteQuery.data?.pages.length) return undefined;
    
    // Aggregate performance metrics from all pages
    const pages = infiniteQuery.data.pages;
    const totalQueryTime = pages.reduce((sum, page) => sum + (page.performance?.queryTime || 0), 0);
    const totalScanned = pages.reduce((sum, page) => sum + (page.pagination.totalScanned || 0), 0);
    const totalReturned = infiniteShows.length;
    const efficiency = totalReturned / Math.max(totalScanned, 1);
    
    return {
      ...pages[pages.length - 1].performance, // Use latest page's performance as base
      queryTime: totalQueryTime,
      efficiency,
      totalScanned,
      totalReturned
    };
  }, [infiniteQuery.data, infiniteShows.length]);

  // Group shows by date using memoization for performance
  const groupedShows = useMemo(() => {
    const shows = useInfiniteScroll
      ? infiniteShows
      : applyDateFilters(legacyQuery.data || []);
    return groupShowsByDate(shows);
  }, [useInfiniteScroll, infiniteShows, legacyQuery.data, applyDateFilters]);

  const infiniteErrorMessage = useMemo(() => {
    if (infiniteError) {
      return infiniteError;
    }

    if (!infiniteQuery.error) {
      return null;
    }

    const error = infiniteQuery.error as unknown;

    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'An error occurred';
  }, [infiniteError, infiniteQuery.error]);

  // Return appropriate interface based on mode
  if (useInfiniteScroll) {
    return {
      shows: infiniteShows,
      groupedShows,
      // Only show the initial skeleton while the first page is pending; avoid re-triggering it on background refetches
      loading: enabled ? infiniteQuery.isPending : false,
      error: infiniteErrorMessage,
      refetch: infiniteQuery.refetch,
      loadMore: infiniteQuery.fetchNextPage,
      hasMore: infiniteQuery.hasNextPage || false,
      isLoadingMore: infiniteQuery.isFetchingNextPage,
      performance,
      pagination: infiniteQuery.data?.pages[infiniteQuery.data.pages.length - 1]?.pagination,
      availableFilters: infiniteQuery.data?.pages[0]?.filters.available,
    };
  }

  // Legacy return for backward compatibility
  return {
    shows: applyDateFilters(legacyQuery.data || []),
    groupedShows,
    loading: enabled ? legacyQuery.isPending : false,
    error: legacyQuery.error?.message || null,
    refetch: legacyQuery.refetch,
  };
}
